<div class="position-relative">
  @if (isLoading) {
    <loader [config]="{ type: 'spinner', overlay: true, size: 'md' }"></loader>
  }

  <p class="small text-muted mb-3">Project costs and profitability. The balance is recalculated after every change.</p>

  <div class="finance-panel">
    <div class="finance-panel-header">
      <h5 class="finance-panel-title">Testing Costs</h5>
      @if (canModify) {
        <button type="button" class="btn btn-success btn-sm" (click)="addTestingCost()">
          <i class="fa fa-solid fa-plus me-1"></i>Add row
        </button>
      }
    </div>
    <div class="finance-panel-body">
      <div class="table-responsive">
        <table class="table table-striped finance-table mb-0">
          <thead>
            <tr>
              <th>Resource / User</th>
              <th>Hours</th>
              <th>Hourly Rate</th>
              <th>Row Total</th>
              <th>Activity Description</th>
              <th>Date</th>
              @if (hasActionsColumn) {
                <th class="text-center">Actions</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (cost of testingCosts; track cost.projectFinanceCostId) {
              <tr>
                <td>
                  {{ resourceName(cost) }}
                </td>
                <td class="finance-amount">{{ cost.hoursWorked }}</td>
                <td class="finance-amount">{{ cost.hourlyRate | currency: currency }}</td>
                <td class="finance-amount fw-semibold">{{ cost.rowTotal | currency: currency }}</td>
                <td>{{ cost.activityDescription || '—' }}</td>
                <td class="text-nowrap">{{ formatDate(cost.date) }}</td>
                @if (hasActionsColumn) {
                  <td class="text-center">
                    <div class="btn-group">
                      @if (canModify) {
                        <button type="button" class="btn btn-sm btn-action btn-primary" ngbTooltip="Edit row" (click)="editTestingCost(cost)">
                          <i class="fa fa-edit"></i>
                        </button>
                      }
                      @if (canDelete) {
                        <button type="button" class="btn btn-sm btn-action btn-danger" ngbTooltip="Delete row" (click)="confirmDeleteTestingCost(cost)">
                          <i class="fa fa-trash"></i>
                        </button>
                      }
                    </div>
                  </td>
                }
              </tr>
            }
            @if (testingCosts.length === 0 && !isLoading) {
              <tr>
                <td [attr.colspan]="hasActionsColumn ? 7 : 6" class="text-center text-muted">No testing costs recorded</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="text-end mt-2">Subtotal: <strong class="finance-amount">{{ testingCostsSubtotal | currency: currency }}</strong></div>
    </div>
  </div>

  <div class="finance-panel">
    <div class="finance-panel-header">
      <h5 class="finance-panel-title">Other Costs</h5>
      @if (canModify) {
        <button type="button" class="btn btn-success btn-sm" (click)="addOtherCost()">
          <i class="fa fa-solid fa-plus me-1"></i>Add row
        </button>
      }
    </div>
    <div class="finance-panel-body">
      <div class="table-responsive">
        <table class="table table-striped finance-table mb-0">
          <thead>
            <tr>
              <th>Category</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Attachment</th>
              <th>Notes</th>
              @if (hasActionsColumn) {
                <th class="text-center">Actions</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (cost of otherCosts; track cost.projectFinanceOtherCostId) {
              <tr>
                <td><span class="badge bg-light text-dark border">{{ categoryLabel(cost.category) }}</span></td>
                <td>{{ cost.description }}</td>
                <td class="finance-amount fw-semibold">{{ cost.amount | currency: currency }}</td>
                <td class="text-nowrap">{{ formatDate(cost.date) }}</td>
                <td>
                  @if (cost.attachment) {
                    <a href="javascript:void(0);" class="text-decoration-none" (click)="openAttachment(cost.attachment)">
                      <i class="fa fa-solid fa-paperclip me-1"></i>{{ cost.attachment.fileNameOriginal }}
                    </a>
                  } @else {
                    —
                  }
                </td>
                <td>{{ cost.notes || '—' }}</td>
                @if (hasActionsColumn) {
                  <td class="text-center">
                    <div class="btn-group">
                      @if (canModify) {
                        <button type="button" class="btn btn-sm btn-action btn-primary" ngbTooltip="Edit row" (click)="editOtherCost(cost)">
                          <i class="fa fa-edit"></i>
                        </button>
                      }
                      @if (canDelete) {
                        <button type="button" class="btn btn-sm btn-action btn-danger" ngbTooltip="Delete row" (click)="confirmDeleteOtherCost(cost)">
                          <i class="fa fa-trash"></i>
                        </button>
                      }
                    </div>
                  </td>
                }
              </tr>
            }
            @if (otherCosts.length === 0 && !isLoading) {
              <tr>
                <td [attr.colspan]="hasActionsColumn ? 7 : 6" class="text-center text-muted">No other costs recorded</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="text-end mt-2">Subtotal: <strong class="finance-amount">{{ otherCostsSubtotal | currency: currency }}</strong></div>
    </div>
  </div>

  <div class="finance-panel mb-0">
    <div class="finance-panel-header">
      <h5 class="finance-panel-title">Balance Summary</h5>
    </div>
    <div class="finance-panel-body">
      @if (balance) {
        <div class="row align-items-center">
          <div class="col-lg-8">
            <div class="row g-3">
              <div class="col-sm-6">
                <div class="balance-label">Total Income (Invoiced)</div>
                <div class="balance-value finance-amount">{{ balance.totalIncome | currency: currency }}</div>
              </div>
              <div class="col-sm-6">
                <div class="balance-label">Total Testing Costs</div>
                <div class="balance-value finance-amount">{{ balance.totalTestingCosts | currency: currency }}</div>
              </div>
              <div class="col-sm-6">
                <div class="balance-label">Total Other Costs</div>
                <div class="balance-value finance-amount">{{ balance.totalOtherCosts | currency: currency }}</div>
              </div>
              <div class="col-sm-6">
                <div class="balance-label">Total Costs</div>
                <div class="balance-value finance-amount">{{ balance.totalCosts | currency: currency }}</div>
              </div>
            </div>
          </div>
          <div class="col-lg-4 balance-margin" [class.is-loss]="!isProfitable">
            <div class="balance-label text-uppercase">Margin / Balance</div>
            <div class="balance-margin-value finance-amount">{{ balance.margin | currency: currency }}</div>
            <span class="balance-status">{{ isProfitable ? 'Profitable' : 'Loss' }}</span>
          </div>
        </div>
      } @else if (!isLoading) {
        <p class="text-muted mb-0">Balance not available</p>
      }
    </div>
  </div>
</div>
